import * as React from 'react'
import styled, { css } from 'styled-components'
import * as api from '@owl-nest/api-client/next'

import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'
import * as consent from '@owl-nest/consent'

type StretchGoalCardProps = {
  goal: React.ReactNode
  title: React.ReactNode
  progress: number
  formattedProgress: React.ReactNode
  description?: string
  className?: string
  blurred?: boolean
  status: api.StretchGoalStatus
}

export function StretchGoalCard({
  className,
  progress,
  formattedProgress,
  goal,
  title,
  description,
  blurred = false,
  status,
}: StretchGoalCardProps) {
  const labelId = React.useId()

  return (
    <Wrapper $blurred={blurred && status === api.StretchGoalStatus.INCOMING} className={className}>
      {status === api.StretchGoalStatus.UNLOCKED ? (
        <Icon $color="green">
          <plume.glyphs.twoToned.Checkbox size={38} />
        </Icon>
      ) : status === api.StretchGoalStatus.INCOMING ? (
        <Icon $color="grey">
          <plume.glyphs.twoToned.Lock size={50} />
        </Icon>
      ) : (
        <Icon $color="blue">
          <plume.glyphs.colored.Hourglass size={50} />
        </Icon>
      )}
      <Body>
        <plume.styles.heading.S>{goal}</plume.styles.heading.S>
        <plume.styles.heading.XS id={labelId}>{title}</plume.styles.heading.XS>
        {(status === api.StretchGoalStatus.UNLOCKED || status === api.StretchGoalStatus.ONGOING) && (
          <>
            <plume.styles.progressBar.ProgressBar
              aria-labelledby={labelId}
              $value={Math.max(Math.min(progress, 100), 0)}
            />
            <Goal>
              <GoalCurrent $color={status === api.StretchGoalStatus.UNLOCKED ? 'green' : 'blue'}>
                {formattedProgress}
              </GoalCurrent>
              <GoalTarget>{goal}</GoalTarget>
            </Goal>
          </>
        )}
        {description && (
          <plume.Toggle
            maxHeight={120}
            translations={{
              showLess: t('Show less'),
              showMore: t('Show more'),
            }}
          >
            <consent.media.Wall rawMarkup={description}>
              <plume.WysiwygContent loading="lazy" $textSize="small" />
            </consent.media.Wall>
          </plume.Toggle>
        )}
      </Body>
    </Wrapper>
  )
}

const GoalBase = styled(plume.styles.copy.S.semiBold).attrs({ as: 'span' })``

const GoalCurrent = styled(GoalBase)<{ $color: 'green' | 'blue' }>`
  color: ${({ $color }) => ($color === 'green' ? plume.COLORS.SECONDARY_GREEN_800 : plume.COLORS.PRIMARY_BLUE_700)};
`

const GoalTarget = styled(GoalBase)`
  color: ${plume.COLORS.PRIMARY_SAND_700};

  &::before {
    content: ' / ';
  }
`

const Goal = styled.div``

const Icon = styled.div<{ $color: 'grey' | 'blue' | 'green' }>`
  border-radius: 50%;
  background-color: ${({ $color }) =>
    $color === 'green'
      ? plume.COLORS.hexToRgb(plume.COLORS.SECONDARY_GREEN_500, 0.12)
      : $color === 'blue'
        ? plume.COLORS.hexToRgb(plume.COLORS.PRIMARY_BLUE_500, 0.12)
        : plume.COLORS.hexToRgb(plume.COLORS.PRIMARY_SAND_500, 0.12)};
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  word-break: break-word;

  ${Goal} {
    margin: 0 0 0 auto;
  }

  ${plume.styles.progressBar.ProgressBar} {
    margin: 16px 0 8px;
  }

  ${plume.styles.heading.XS} {
    margin-top: 4px;
  }

  ${plume.styles.toggle.ToggleButton} {
    justify-content: center;
  }
`

const Wrapper = styled.div<{ $blurred?: boolean }>`
  padding: 16px;
  flex-direction: row;
  box-shadow: ${plume.EFFECTS.SHADOW_3};

  ${Icon} {
    flex: 0 0 46px;
    margin-right: 16px;
    height: 46px;
    width: 46px;
    margin-bottom: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  ${Body} {
    flex: 1 1 100%;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    display: flex;
  }

  ${({ $blurred }) => {
    if ($blurred) {
      return css`
        position: relative;

        &::before {
          content: '';
          inset: 0;
          position: absolute;
          backdrop-filter: blur(12px);
          z-index: ${plume.ZINDEX.HIGH};
        }
      `
    }
  }}
`
